import { useZone } from "@/lib/facility/zone";
import { formatDateOnly, toDateInput, errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import type { ApiKeyScope } from "@alm/domain";
import { env } from "@/lib/env";
import { ApiKeyForm } from "./-components/integrations/api-key-form";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";

export const Route = createFileRoute("/_authenticated/_administration/settings/api-keys")({
  beforeLoad: ({ context }) => guardRoute(context.me, "api-keys"),
  component: ApiKeysSettings,
});

type ApiKey = Output<typeof orpcClient.administration.apiKeys.list>[number];

const fail = (error: Error) => toast.error(errorWords(error));
const expired = (value: Date | string | null) => value !== null && new Date(value) < new Date();
const when = (value: Date | string | null, zone: string) =>
  value ? formatDateOnly(value, zone) : null;

/** The secret is shown once and stored as a hash, so nobody can read it back, including its issuer.
 * Losing one means rotating it. */
function ApiKeysSettings() {
  const zone = useZone();
  const canManage = useCan("settings:manage");
  const queryClient = useQueryClient();
  const [issuing, setIssuing] = useState(false);
  const [secret, setSecret] = useState<{ name: string; apiKey: string } | null>(null);
  const [rotating, setRotating] = useState<ApiKey | null>(null);
  const [renaming, setRenaming] = useState<ApiKey | null>(null);
  const [removing, setRemoving] = useState<ApiKey | null>(null);

  const keys = useQuery(orpc.administration.apiKeys.list.queryOptions({}));
  const facilities = useQuery(orpc.administration.facilities.list.queryOptions({ input: {} }));
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.apiKeys.key() });

  const create = useMutation(
    orpc.administration.apiKeys.create.mutationOptions({
      onSuccess: async (issued) => {
        setIssuing(false);
        setSecret({ name: issued.name, apiKey: issued.apiKey });
        await invalidate();
      },
      onError: fail,
    }),
  );
  const rotate = useMutation(
    orpc.administration.apiKeys.rotate.mutationOptions({
      onSuccess: async (issued) => {
        setRotating(null);
        setSecret({ name: issued.name, apiKey: issued.apiKey });
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.administration.apiKeys.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Key updated");
        setRenaming(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.administration.apiKeys.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Key revoked");
        setRemoving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = keys.data ?? [];

  return (
    <>
      <PageHeader
        title="API keys"
        meta="What an outside system signs in with, what it may do, and where it reaches."
      />

      <RecordCard
        addLabel="Issue key"
        canWrite={canManage}
        count={keys.data ? rows.length : undefined}
        onAdd={() => setIssuing(true)}
        title="Keys"
      >
        {keys.isPending ? (
          <RowsSkeleton />
        ) : keys.isError ? (
          <ReadFailed
            message="The keys could not be loaded."
            error={keys.error}
            retrying={keys.isFetching}
            onRetry={() => void keys.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyRows text="No keys issued. An integration needs one before it can read anything." />
        ) : (
          <ul className="divide-y">
            {rows.map((row) => (
              <RecordRow
                key={row.id}
                meta={
                  <>
                    <code>{row.keyPrefix}…</code>
                    {", "}
                    {row.scopes.join(", ")}
                    {row.expiresAt ? `, expires ${when(row.expiresAt, zone) ?? ""}` : ""}
                    {row.lastUsedAt
                      ? `, last used ${when(row.lastUsedAt, zone) ?? ""}`
                      : ", never used"}
                  </>
                }
                title={
                  <span className="flex items-center gap-2">
                    {row.name}
                    {row.isActive ? null : <Badge variant="outline">Off</Badge>}
                    {expired(row.expiresAt) ? <Badge variant="outline">Expired</Badge> : null}
                    <Badge variant="secondary">
                      {row.facilityId
                        ? (facilities.data?.find((facility) => facility.id === row.facilityId)
                            ?.name ?? "One building")
                        : "Every building"}
                    </Badge>
                  </span>
                }
                actions={
                  canManage ? (
                    <>
                      <Switch
                        aria-label={`${row.name} active`}
                        checked={row.isActive}
                        onCheckedChange={(next) =>
                          update.mutate({ apiKeyId: row.id, isActive: next })
                        }
                      />
                      <RowActions
                        modal={false}
                        label={row.name}
                        primary={{ label: "Edit", onSelect: () => setRenaming(row) }}
                        items={[
                          { label: "Rotate key", onSelect: () => setRotating(row) },
                          {
                            label: "Revoke key",
                            destructive: true,
                            onSelect: () => setRemoving(row),
                          },
                        ]}
                      />
                    </>
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <Dialog onOpenChange={(open) => !open && setIssuing(false)} open={issuing}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Issue key</DialogTitle>
            <DialogDescription>
              The secret is shown once and never again. Have somewhere to put it before you press
              the button.
            </DialogDescription>
          </DialogHeader>
          <ApiKeyForm
            facilities={facilities.data ?? []}
            onSubmit={(values) => create.mutate(values)}
            pending={create.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog onOpenChange={(open) => !open && setSecret(null)} open={secret !== null}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="size-5" />
              {secret?.name}
            </DialogTitle>
            <DialogDescription>
              Copy this now. It is stored as a hash, so nobody, including you, can read it back.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted flex items-center gap-2 rounded-md border p-3">
            <code className="min-w-0 flex-1 text-sm break-all">{secret?.apiKey}</code>
            <Button
              onClick={() => {
                if (!secret) return;
                void navigator.clipboard.writeText(secret.apiKey);
                toast.success("Key copied");
              }}
              size="icon"
              variant="ghost"
              aria-label="Copy the key"
            >
              <Copy className="size-4" />
            </Button>
          </div>
          {/* The one thing everybody does next, with the address and the header
              spelled out — otherwise the first call is a guess at both. */}
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm">Try it:</p>
            <pre className="bg-muted overflow-x-auto rounded-md border p-3 text-xs">
              {`curl -H "Authorization: Bearer ${secret?.apiKey ?? ""}" \\\n  ${env.WEB__API_URL}/api/v1/residents`}
            </pre>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog onOpenChange={(open) => !open && setRenaming(null)} open={renaming !== null}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit “{renaming?.name}”</DialogTitle>
            <DialogDescription>
              The secret does not change. To replace it, rotate the key instead.
            </DialogDescription>
          </DialogHeader>
          {renaming ? (
            <ApiKeyForm
              key={renaming.id}
              facilities={facilities.data ?? []}
              defaults={{
                name: renaming.name,
                facilityId: renaming.facilityId,
                scopes: renaming.scopes as ApiKeyScope[],
                expiresAt: toDateInput(renaming.expiresAt),
              }}
              submitLabel="Save key"
              pending={update.isPending}
              onSubmit={(values) =>
                update.mutate({
                  apiKeyId: renaming.id,
                  name: values.name,
                  scopes: values.scopes,
                  expiresAt: values.expiresAt ? new Date(values.expiresAt) : null,
                })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={rotating !== null} onOpenChange={(next) => !next && setRotating(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate “{rotating?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The current secret stops working immediately and a new one is shown once. Whatever is
              using the old one will start failing until it is given the new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Leave it</AlertDialogCancel>
            <AlertDialogAction
              disabled={rotate.isPending}
              onClick={() => rotating && rotate.mutate({ apiKeyId: rotating.id })}
            >
              Rotate key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={removing !== null} onOpenChange={(next) => !next && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke “{removing?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops working at once and cannot be brought back. If you only want to pause it,
              switch it off instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => removing && remove.mutate({ apiKeyId: removing.id })}
            >
              Revoke key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
